<template>
    <v-container fluid>
        <v-row justify="center">
            <v-col cols="12">
                <v-card>
                    <v-card-title class="d-block text-center font-weight-bold primary warning--text py-2" style="word-break: normal">
                        PARTICIPANTES DEL SORTEO
                    </v-card-title>
                </v-card>
            </v-col>
        </v-row>
        <v-row justify="center">
            <v-col cols="12">
                <v-card>
                    <v-card-text class="pb-0">
                        <v-row justify="center" align="center">
                            <v-col cols="12" sm="4" md="3">
                                <v-text-field v-model="filters.month" type="month" label="Mes" prepend-icon="mdi-calendar" hide-details/>
                            </v-col>
                            <v-col cols="12" sm="4" md="3">
                                <v-select v-model="filters.period" :items="periods" label="Periodo" prepend-icon="mdi-calendar-range" hide-details/>
                            </v-col>
                            <v-col cols="12" sm="4" md="2">
                                <v-btn color="primary" block large depressed :loading="loading" @click="getData">
                                    <v-icon>mdi-filter</v-icon>&nbsp;&nbsp;Filtrar
                                </v-btn>
                            </v-col>
                        </v-row>
                    </v-card-text>
                    <v-card-subtitle class="text-center font-weight-bold" v-if="period">
                        {{ period.label }} · Minimo {{ threshold }} USD · {{ desserts.length }} participantes inscritos
                    </v-card-subtitle>
                    <v-card-title class="pt-0">
                        <v-text-field v-model="search" append-icon="mdi-magnify" label="Buscar" single-line hide-details/>
                    </v-card-title>
                    <v-data-table :headers="tableHeaders" :items="desserts" :search="search" :loading="loading" :footer-props="footerProps" no-data-text="No hay participantes inscritos y validos para este periodo">
                        <template v-slot:item.total_usd="{ item }">
                            <strong>{{ Number(item.total_usd).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }} USD</strong>
                        </template>
                        <template v-slot:item.transfer_ids="{ item }">
                            {{ item.transfer_ids.join(', ') }}
                        </template>
                        <template v-slot:item.actions="{ item }">
                            <v-btn color="error" small depressed :loading="removingKey === participantKey(item)" :disabled="user.role !== 'Administrator'" @click="askRemove(item)">
                                <v-icon small>mdi-account-minus</v-icon>&nbsp;Excluir
                            </v-btn>
                        </template>
                    </v-data-table>
                </v-card>
            </v-col>
        </v-row>
        <v-row justify="center">
            <v-col cols="12">
                <v-card>
                    <v-card-title class="d-flex align-center">
                        <span class="font-weight-bold">Participantes excluidos</span>
                        <v-spacer/>
                        <v-chip color="warning" text-color="white" small>{{ excluded.length }}</v-chip>
                    </v-card-title>
                    <v-card-subtitle>
                        Estos clientes no entran al sorteo del periodo filtrado. Puedes restaurarlos si fueron excluidos por error.
                    </v-card-subtitle>
                    <v-card-title class="pt-0">
                        <v-text-field v-model="excludedSearch" append-icon="mdi-magnify" label="Buscar excluido" single-line hide-details/>
                    </v-card-title>
                    <v-data-table :headers="excludedHeaders" :items="excluded" :search="excludedSearch" :loading="loading" :footer-props="footerProps" no-data-text="No hay participantes excluidos para este periodo">
                        <template v-slot:item.total_usd="{ item }">
                            <strong>{{ Number(item.total_usd).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }} USD</strong>
                        </template>
                        <template v-slot:item.transfer_ids="{ item }">
                            {{ item.transfer_ids.join(', ') || 'Sin operaciones actuales' }}
                        </template>
                        <template v-slot:item.actions="{ item }">
                            <v-btn color="success" small depressed :loading="restoringKey === participantKey(item)" :disabled="user.role !== 'Administrator'" @click="restoreParticipant(item)">
                                <v-icon small>mdi-account-check</v-icon>&nbsp;Restaurar
                            </v-btn>
                        </template>
                    </v-data-table>
                </v-card>
            </v-col>
        </v-row>

        <v-dialog v-model="removeDialog" max-width="520">
            <v-card>
                <v-card-title class="font-weight-bold" style="word-break: normal">
                    Excluir participante del sorteo
                </v-card-title>
                <v-card-text v-if="selected">
                    <v-alert type="warning" outlined>
                        Esta accion excluye a <strong>{{ selected.name }}</strong> del sorteo de
                        <strong>{{ period ? period.label : 'este periodo' }}</strong>. No elimina sus operaciones ni su usuario.
                    </v-alert>
                    <div class="grey--text text--darken-2">
                        Cliente #{{ selected.user_id }} · {{ selected.source }} ·
                        {{ Number(selected.total_usd).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }} USD
                    </div>
                </v-card-text>
                <v-card-actions>
                    <v-spacer/>
                    <v-btn text :disabled="!!removingKey" @click="removeDialog = false">Cancelar</v-btn>
                    <v-btn color="error" depressed :loading="!!removingKey" @click="removeParticipant">
                        <v-icon small>mdi-account-minus</v-icon>&nbsp;&nbsp;Excluir
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import axios from 'axios';
import Form from '../../../helpers/form';

export default {
    name: 'Participants',
    mixins: [Form],
    props: ['user'],
    data: () => ({
        loading: false,
        removingKey: null,
        restoringKey: null,
        removeDialog: false,
        selected: null,
        search: '',
        excludedSearch: '',
        threshold: 50,
        period: null,
        filters: {
            month: null,
            period: null
        },
        periods: [
            { text: '1 al 15', value: 'first' },
            { text: '16 al ultimo dia', value: 'second' }
        ],
        headers: [
            { text: 'Origen', value: 'source', sortable: false },
            { text: 'ID Cliente', value: 'user_id', sortable: false },
            { text: 'Cliente', value: 'name', sortable: false },
            { text: 'Pais', value: 'country', sortable: false },
            { text: 'Total USD', value: 'total_usd', align: 'end', sortable: true },
            { text: 'Operaciones', value: 'transfers_count', align: 'center', sortable: true },
            { text: 'Transferencias', value: 'transfer_ids', sortable: false }
        ],
        desserts: [],
        excluded: [],
        footerProps: {
            itemsPerPageOptions: [50, 100, 250, 500, -1]
        }
    }),
    computed: {
        tableHeaders() {
            if (this.user.role !== 'Administrator') {
                return this.headers;
            }

            return this.headers.concat([
                { text: 'Acciones', value: 'actions', sortable: false, align: 'center' }
            ]);
        },
        excludedHeaders() {
            if (this.user.role !== 'Administrator') {
                return this.headers;
            }

            return this.headers.concat([
                { text: 'Restaurar', value: 'actions', sortable: false, align: 'center' }
            ]);
        }
    },
    mounted() {
        this.setDefaultPeriod();
        this.getData();
    },
    methods: {
        setDefaultPeriod() {
            const now = new Date();
            this.filters.month = now.toISOString().slice(0, 7);
            this.filters.period = now.getDate() > 15 ? 'second' : 'first';
        },
        getData() {
            this.loading = true;
            axios.get('/api/lottery/participants', { params: this.filters }).then(res => {
                this.desserts = res.data.participants;
                this.excluded = res.data.excluded || [];
                this.threshold = res.data.threshold_usd;
                this.period = res.data.period;
            }).catch(err => {
                this.handleErrors(err.response.data.errors);
            }).then(() => {
                this.loading = false;
            });
        },
        participantKey(item) {
            return item.source + ':' + item.user_id;
        },
        askRemove(item) {
            this.selected = item;
            this.removeDialog = true;
        },
        removeParticipant() {
            if (!this.selected) {
                return;
            }

            this.removingKey = this.participantKey(this.selected);
            axios.post('/api/lottery/participants/remove', Object.assign({}, this.filters, {
                source: this.selected.source,
                user_id: this.selected.user_id
            })).then(res => {
                if (res.data.v === 'success') {
                    this.$toast.success(res.data.status, { icon: 'mdi-check-circle' });
                    this.removeDialog = false;
                    this.selected = null;
                    this.getData();
                } else {
                    this.$toast.warning(res.data.status, { icon: 'mdi-information', timeout: 8000 });
                }
            }).catch(err => {
                this.handleErrors(err.response.data.errors);
            }).then(() => {
                this.removingKey = null;
            });
        },
        restoreParticipant(item) {
            this.restoringKey = this.participantKey(item);
            axios.post('/api/lottery/participants/restore', Object.assign({}, this.filters, {
                source: item.source,
                user_id: item.user_id
            })).then(res => {
                if (res.data.v === 'success') {
                    this.$toast.success(res.data.status, { icon: 'mdi-check-circle' });
                    this.getData();
                } else {
                    this.$toast.warning(res.data.status, { icon: 'mdi-information', timeout: 8000 });
                }
            }).catch(err => {
                this.handleErrors(err.response.data.errors);
            }).then(() => {
                this.restoringKey = null;
            });
        }
    }
}
</script>
